const GLOBAL_APP_URL =
  import.meta.env.VITE_REACT_APP_GLOBAL_APP_URL ||
  'https://app.nudgeforme.com';

const EU_APP_URL =
  import.meta.env.VITE_REACT_APP_EU_APP_URL ||
  'https://europe.nudgeforme.com';

function cleanRegion(value = '') {
  const clean = String(value || '').trim().toLowerCase();

  if (
    ['eu', 'europe', 'prod-eu', 'europe-west1', 'ooo-maker-eu'].includes(clean)
  ) {
    return 'eu';
  }

  if (
    ['global', 'us', 'prod', 'production', 'us-central1', 'ooo-maker'].includes(clean)
  ) {
    return 'global';
  }

  return '';
}

export function normalizeRegion(value = '') {
  return cleanRegion(value);
}

/**
 * Detect which deployment is ACTUALLY running.
 *
 * Important:
 * Explicit region helpers such as appUrlForRegion('eu') must NOT use this
 * function, because the current Global build would override the requested
 * region and incorrectly return Global.
 */
export function getRuntimeRegion(fallbackRegion = '') {
  const envRegion = cleanRegion(import.meta.env.VITE_REACT_APP_REGION);
  if (envRegion) return envRegion;

  const environment = String(
    import.meta.env.VITE_REACT_APP_ENVIRONMENT || '',
  ).toLowerCase();

  if (environment.includes('eu')) return 'eu';

  const projectId = String(
    import.meta.env.VITE_REACT_APP_FIREBASE_PROJECT_ID || '',
  ).toLowerCase();

  if (projectId === 'ooo-maker-eu') return 'eu';
  if (projectId === 'ooo-maker') return 'global';

  if (typeof window !== 'undefined') {
    const hostname = String(window.location.hostname || '').toLowerCase();

    if (
      hostname === 'europe.nudgeforme.com' ||
      hostname === 'europe.nudgefor.me' ||
      hostname.startsWith('europe.')
    ) {
      return 'eu';
    }

    if (
      hostname === 'app.nudgeforme.com' ||
      hostname === 'app.nudgefor.me' ||
      hostname.startsWith('app.')
    ) {
      return 'global';
    }
  }

  return cleanRegion(fallbackRegion) || 'global';
}

export function regionFromRuntime(fallbackRegion = '') {
  return getRuntimeRegion(fallbackRegion);
}

/**
 * Resolve an EXPLICIT region if supplied.
 * Only fall back to runtime detection when no region was given.
 */
function resolveRegion(region = '') {
  return cleanRegion(region) || getRuntimeRegion();
}

export function oppositeRegion(region = '') {
  return resolveRegion(region) === 'eu' ? 'global' : 'eu';
}

export function getOppositeRegion(region = '') {
  return oppositeRegion(region);
}

export function regionDisplayName(region = '') {
  return resolveRegion(region) === 'eu' ? 'Europe' : 'Global';
}

export function regionShortLabel(region = '') {
  return resolveRegion(region) === 'eu' ? 'EU' : 'Global';
}

export function getRuntimeRegionLabel(fallbackRegion = '') {
  return getRuntimeRegion(fallbackRegion) === 'eu'
    ? 'Europe environment'
    : 'Global environment';
}

export function getRuntimeRegionShortLabel(fallbackRegion = '') {
  return getRuntimeRegion(fallbackRegion) === 'eu'
    ? 'EU'
    : 'Global';
}

export function getRegionAppUrl(region = '') {
  return resolveRegion(region) === 'eu'
    ? EU_APP_URL
    : GLOBAL_APP_URL;
}

export function appUrlForRegion(region = '') {
  return getRegionAppUrl(region).replace(/\/+$/, '');
}

export function getOppositeRegionAppUrl(region = '') {
  return appUrlForRegion(oppositeRegion(region));
}

export function oppositeRegionAppUrl(region = '') {
  return getOppositeRegionAppUrl(region);
}

export function currentAppUrl(fallbackRegion = '') {
  if (typeof window !== 'undefined') {
    const origin = String(window.location.origin || '').replace(/\/+$/, '');

    if (origin) {
      return origin;
    }
  }

  return appUrlForRegion(fallbackRegion);
}